import React from "react";
import {
  Box,
  ButtonBase,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import styles from "./MyLearningWidget.module.scss";
import DataLoader from "../../../../components/custom-data-loader/DataLoader";
import MyLearningCustomSkeleton from "./MyLearningCustomSkeleton";
import { ArrowIcon } from "../../../../components/icon-components/ArrowIcon";
import { useGetMyLearningWidgetContentQuery } from "./services/GetMyLearningAPISlice";
import { useAppDispatch, useAppSelector } from "../../../../store/hooks";
import CustomSwiper from "../../../../components/v2/custom-swiper-layout/CustomSwiper";
import LearningCard from "./LearningCard";
import { MyLearningWidgetData } from "../../../../utility/interfaces/educationLibraryInterfaces";
import { useTranslation } from "react-i18next";
import {
  CONTENT_STATUS_COMPLETED_ID,
  SAVE_CLICK_STATUS_ID,
} from "./../../../../utility/constants/common-constants";
import {
  getPatientRoute,
  handleExternalLinkClick,
} from "../../../../utility/functions/common-functions";
import { Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import { useSaveContentClickMutation } from "../../../../features/view-learning-content/services/SaveContentStatusAccessLogAPISlice";
import { useSaveContentStatusMutation } from "../../../../features/view-learning-content/services/SaveContentStatusAPISlice";
import { setLearningContent } from "../../../../features/view-learning-content/services/LearningContentDataSlice";
import { useLocation, useNavigate } from "react-router-dom";
import EmptyComponent from "../../../../components/v2/custom-empty-error-component/EmptyComponent";

const MyLearningWidget: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const theme = useTheme();
  const { values } = theme.breakpoints;
  const isSmallScreen = useMediaQuery("(max-width:900px)");

  const {
    patientProfileInfo: {
      patientProfileId,
      userName: patientUserName,
    },
  } = useAppSelector((state) => state.patientProfileDetails);

  const patientPreferredLang = useAppSelector(
    (state) => state.patientOnboardingStatus.preferredLanguage
  );

  const [saveContentClick] = useSaveContentClickMutation();

  const [contentStatus] = useSaveContentStatusMutation();

  const {
    data: myLearningWidgetData,
    isLoading: myLearningWidgetLoading,
    error: myLearningWidgetDataError,
  } = useGetMyLearningWidgetContentQuery({
    patientId: patientProfileId,
    preferredLanguage: patientPreferredLang,
  });

  const showContetntDetails = async (content: MyLearningWidgetData) => {
    // Handle external link content as before
    if (content?.contentTypeCode === "URL") {
      if (content?.milestoneLearningUrls) {
        window.open(
          content?.milestoneLearningUrls,
          "_blank",
          "noopener,noreferrer"
        );
        if (content?.activityCompletionStatus !== t("completed")) {
          handleExternalLinkClick(
            content.casePlanMilestoneMappingId,
            CONTENT_STATUS_COMPLETED_ID,
            patientUserName || "Patient",
            patientProfileId,
            patientPreferredLang,
            dispatch,
            contentStatus
          );
        }
      }
    } else {
      // Dispatch content to Redux
      dispatch(
        setLearningContent({
          type: content?.contentTypeCode,
          category: content?.activitySubCategoryName,
          isNew: content?.isNew,
          videoUrl: content?.learningContentDto?.videoUrl,
          fullHtml: content?.learningContentDto?.fullHtml,
          title: content?.activityDisplayName,
          summarry: content?.learningContentDto?.summary,
          casePlanMilestoneMappingId: content?.casePlanMilestoneMappingId,
          activityCompletionStatus: content?.activityCompletionStatus,
          patientProfileId,
          lang: content?.learningContentDto?.contentMetaLang,
          activityPoints: content?.activityPoints,
          thumbnail: content?.learningContentDto?.thumbnail,
          length: content?.learningContentDto?.length,
          educationalContentSourceId: content?.educationalContentSourceId,
          educationalContentIdentifier: content?.contentCode.toString(),
          navigationInfo: {
            isViewLearningContentEnabled: true,
            arrivedFrom: location.pathname
          }
        })
      );

      // Navigate to the learning view
      navigate(getPatientRoute(patientProfileId, "/learning"), {
        state: {
          selectedTabId: 5,
        },
      });
    }

    // Save click mutation
    try {
      const body = {
        casePlanMilestoneMappingId: content?.casePlanMilestoneMappingId || "",
        patientProfileId: patientProfileId,
        planId: 1,
        caseId: 1,
        milestoneId: 1,
        contentIdentifier: content?.contentCode.toString(),
        contentWatchedDurationInMins: 1,
        action: SAVE_CLICK_STATUS_ID,
        educationalContentSourceId: content?.educationalContentSourceId,
      };

      await saveContentClick({
        body,
        patientPreferredLang,
      }).unwrap();
    } catch (err: any) {
      console.log(err, "saveContentClick");
    }
  };

  const handleClick = () => {
    //Click to details page
    navigate(getPatientRoute(patientProfileId, "/learning"), {
      state: { selectedTabId: 5 },
    });
  };

  return (
    <>
      <Box className={styles.myLearningContainer}>
        <Box>
          <ButtonBase onClick={handleClick}>
            <Typography variant="h3" mb={3}>
              {t("educationContent.myLearning")}
              <ArrowIcon
                width={20}
                height={20}
                style={{ transform: "rotate(180deg)", marginLeft: 8 }}
              />
            </Typography>
          </ButtonBase>
        </Box>
        <DataLoader
          isLoading={myLearningWidgetLoading}
          error={myLearningWidgetDataError}
          data={myLearningWidgetData}
          loadingComponent={<MyLearningCustomSkeleton />}
          errorComponent={
            <EmptyComponent
              message={t("educationContent.learningWidgetError")}
              textColor="error"
            />
          }
          emptyComponent={
            <EmptyComponent message={t("noLearningWidgetData")} />
          }
        >
          {() => (
            <Box className={styles.swiperWrapper}>
              <CustomSwiper<MyLearningWidgetData>
                items={myLearningWidgetData}
                modules={[Pagination]}
                swiperOptions={{
                  pagination: !isSmallScreen
                    ? {
                        clickable: true,
                        el: `.${styles.customSwiperPagination}`,
                      }
                    : false,
                  slidesPerView: 1.2,
                  spaceBetween: 2,
                  breakpoints: {
                    [values.xs]: {
                      slidesPerView: 1.8,
                      spaceBetween: 2,
                    },
                    [values.sm]: {
                      slidesPerView: 2,
                      spaceBetween: 2,
                    },
                    [values.md]: {
                      slidesPerView: 3,
                      spaceBetween: 2,
                    },
                    [values.lg]: {
                      slidesPerView: 3.8,
                      spaceBetween: 2,
                    },
                    [values.xl]: {
                      slidesPerView: 4,
                      spaceBetween: 2,
                    },
                  },
                }}
                renderSlide={(educationContent, index) => {
                  const content = educationContent.learningContentDto;
                  return (
                    <LearningCard
                      key={index}
                      imageSrc={content?.thumbnail}
                      imageAlt={content?.title}
                      isNew={educationContent?.isNew}
                      duration={content?.length?.toString()}
                      title={educationContent?.activityDisplayName}
                      width="350px"
                      height="300px"
                      contentType={educationContent?.contentTypeCode}
                      activityPoints={educationContent.activityPoints}
                      activityCompletionStatus={
                        educationContent.activityCompletionStatus
                      }
                      onButtonClick={() =>
                        showContetntDetails(educationContent)
                      }
                    />
                  );
                }}
              />
              {!isSmallScreen && (
                <div className={styles.customSwiperPagination} />
              )}
            </Box>
          )}
        </DataLoader>
      </Box>
    </>
  );
};

export default MyLearningWidget;
